import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Spinner } from '@cad-arc';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventPanelFormType, SubdivisionOption } from '@mark43/cad-api';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { getEventSubdivisionFieldsParams } from '../../common/helpers/get-event-subdivision-fields-params';
import { EventClassPrefix, SubdivisionFieldName, SubdivisionFieldParam } from '../../common/types';

export function SubdivisionFields(props: {
    eventClassPrefix: EventClassPrefix;
    form: UseFormReturn<EventPanelFormType>;
    asDataListItem?: boolean;
}): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const bootstrapState = useBootstrapState();

    const allSubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;
    const agencySubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse
            ?.agencyIdToSubdivisionOptionsBootstrapView;

    const agencyFieldName:
        | 'policeAgency'
        | 'fireAgency'
        | 'emsAgency' = `${props.eventClassPrefix}Agency`;

    const agencyValue = useWatch({
        control: props.form.control,
        name: agencyFieldName,
    });

    const fieldParams: SubdivisionFieldParam[] | undefined = React.useMemo(() => {
        return getEventSubdivisionFieldsParams({
            eventClassPrefix: props.eventClassPrefix,
            agencyValue,
            multiAgencySubdivisions: enabledFeatures.multiAgencySubdivisions,
            allSubdivisionOptions,
            agencySubdivisionOptions,
        });
    }, [
        props.eventClassPrefix,
        agencyValue,
        enabledFeatures,
        allSubdivisionOptions,
        agencySubdivisionOptions,
    ]);

    if (!fieldParams) {
        return <Spinner />;
    }

    return (
        <>
            {fieldParams.map((fieldParam, index) => {
                return (
                    <SubdivisionField
                        asDataListItem={props.asDataListItem}
                        name={fieldParam.name}
                        options={fieldParam.options}
                        label={fieldParam.label}
                        index={index}
                        form={props.form}
                        key={fieldParam.name}
                        eventClassPrefix={props.eventClassPrefix}
                    />
                );
            })}
        </>
    );
}

function SubdivisionField(props: {
    form: UseFormReturn<EventPanelFormType>;
    name: SubdivisionFieldName;
    options: SubdivisionOption[];
    label: string | undefined;
    index: number;
    eventClassPrefix: EventClassPrefix;
    asDataListItem?: boolean;
}): React.ReactElement {
    const { name, options, label } = props;
    if (!options.length) {
        return <></>;
    }
    return (
        <FormSelect
            length="sm"
            asEditable={false}
            asDataListItem={props.asDataListItem}
            key={name}
            control={props.form.control}
            label={label}
            name={name}
            options={options}
            eventNotificationName="SUBDIVISION"
        />
    );
}
